/**
 * Shared table column configurations for PACS and Worklist tables
 * Following DRY principle to avoid code duplication
 */

import { Tag, Button, Tooltip, Dropdown, Menu, Select, message } from 'antd';
import React from 'react';

import {
  PrinterOutlined,
  EyeOutlined,
  ExportOutlined,
  FileWordOutlined,
  EditOutlined,
  ClockCircleOutlined,
  FlagOutlined,
  DeleteOutlined,
  SplitCellsOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
// Flat multi-color illustrated icons for the leading action-column buttons.
import {
  NotesIcon,
  SeriesIcon,
  MoreIcon,
  InfoIcon,
  HistoryIcon,
  DownloadIcon,
  ViewerIcon,
  UploadIcon,
  ACTION_HOVER,
  ReportIcon
} from '../components/ActionIcons';
import type { ReportTone } from '../components/ActionIcons';
import { formatAgeShort, getUserDetails, hasPermission, getMlcTooltip,hasReportingPermission } from './helper';
import { StudyDateTime } from '../components/StudyDateTime';
import { CENTER_RADIOLOGY_URL } from '../constants/APP_CONSTANTS';
import { FieldLabels, Permissions, getPacsStatusLabel, getPatientIdLabel } from '../constants/fieldLabels';
import { ReReportedBadge } from '../components/ReReportedBadge';
import { ReportingNowBadge } from '../components/ReportingNowBadge';
import { HisStatusReconcileCell } from '../components/ReconcileCells';
import { openReport, isUnconfirmedReportingBlocked } from './tableActionHandlers';
import { getStatusBadgeStyle } from './statusBadgeStyle';

interface ActionColumnConfig {
  openViewer?: (record: any) => void;
  showOrdDetails?: (record: any) => void;
  openRadDesk?: (record: any) => void;
  openReportEditor: (record: any) => void;
  addFile?: (record: any) => void;
  viewNotes: (record: any) => void;
  printReport?: (record: any) => void;
  downloadDocxReport?: (record: any) => void;
  viewReport?: (record: any) => void;
  editSignedOffReport?: (record: any) => void;
  handleShareStudy?: (record: any) => void;
  handleDownloadStudy?: (record: any) => void;
  handleExportStudy?: (record: any) => void;
  editStudyDetails?: (record: any) => void;
  showStudyHistory?: (record: any) => void;
  // handleSelectSeries?: (record: any) => void;
  handleSeriesInfo?: (record: any) => void; // read-only series/image-count dialog (technician)
  splitStudy?: (record: any) => void;
  assignToSelfTechnician?: (record: any, idx: number) => void;
  toggleReporting?: (record: any, idx: number) => void;
  toggleConfirmation?: (record: any, idx: number) => void;
  toggleFeatures?: (record: any, idx: number, feature: string) => void;
  markMlc?: (record: any) => void;
  deleteOrder?: (record: any) => void;
  canDeleteOrder?: boolean; // gates the Delete action (env flag + role)
  role?: string;
  pageType?: 'pacs' | 'worklist'; // Determines which actions to show
}

// Hover-pill tint CSS var for a `.action-icon-button` span, keyed by action.
const hoverVar = (key: string): React.CSSProperties =>
  ({ ['--ai-hover' as any]: ACTION_HOVER[key] } as React.CSSProperties);


const iconButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minWidth: '28px',
  minHeight: '28px',
};

const iconButtonHoverStyle = {
  ':hover': {
    backgroundColor: '#f0f0f0',
  },
};

const disabledIconButtonStyle = {
  ...iconButtonStyle,
  cursor: 'not-allowed',
  opacity: 0.5,
};

/**
 * Calculate dynamic width for actions column based on number of icons
 * @param config - Action column configuration
 * @returns Width in pixels
 */
export const calculateActionsColumnWidth = (config: Partial<ActionColumnConfig>): number => {
  const isPacsPage = config.pageType === 'pacs';

  if (!isPacsPage) {
    // Worklist page - minimal width
    return 80;
  }

  let iconCount = 0;
  const userDetails = getUserDetails();

  // Technicians keep only the More dropdown in the Actions column (the notes /
  // info / history icons live in their own leading columns). Width must still
  // fit the "Actions" header label + the configurator gear.
  if (userDetails?.user_type === 'technician') {
    return 90;
  }

  // Count maximum possible icons that could be shown

  // View Notes (always visible)
  iconCount++;

  // Open Reporting (non-technician)
  if (userDetails?.user_type !== 'technician') {
    iconCount++;
  }

  // Share Study (always available on PACS)
  if (config.handleShareStudy) {
    iconCount++;
  }

  // Select Series (always available on PACS)
  // if (config.handleSelectSeries) {
  //   iconCount++;
  // }

  // More Actions dropdown (always visible on PACS)
  iconCount++;

  // Calculate width: (icon_width * count) + (gap * (count - 1)) + padding
  // Each icon button: 28px, gap between icons: 8px (4px margin each side), container padding: 12px (6px left + 6px right)
  const iconWidth = 34;
  const gapWidth = 8;
  const containerPadding = 12;

  const calculatedWidth = iconWidth * iconCount + gapWidth * (iconCount - 1) + containerPadding;

  // Add some buffer for safety (12px)
  return calculatedWidth;
};

/**
 * Reporting-lifecycle tint for the leading Open-Reporting icon: magenta until
 * anything is written, amber once drafted, orange once reviewed, green once signed
 * off. Anything else (SCANNED, or a status we don't recognise) counts as unreported.
 */
export const getReportIconTone = (pacsStatus?: string): ReportTone => {
  switch (pacsStatus) {
    case 'DRAFTED':
      return 'drafted';
    case 'REVIEWED':
      return 'reviewed';
    case 'SIGNEDOFF':
      return 'signedoff';
    default:
      return 'unreported';
  }
};

/** Tooltip wording per tone, so the color has a text equivalent. */
export const REPORT_TONE_LABEL: Record<ReportTone, string> = {
  unreported: 'Open Report Editor — not reported',
  drafted: 'Open Report Editor — drafted',
  reviewed: 'Open Report Editor — reviewed',
  signedoff: 'Open Report Editor — signed off',
};

/**
 * Status badge styling. The implementation lives in utils/statusBadgeStyle so
 * PACS, Worklist, Orders, Dispatch, Front Desk, QC and the modals all render
 * the same pill; re-exported here because several of them import it from this
 * module.
 */
export const getStatusStyle = getStatusBadgeStyle;

/**
 * Build the "More Actions" dropdown menu for a row. The high-frequency actions
 * (View Notes, Patient & Order Info, Download Study, Open Viewer, Select Series)
 * are surfaced as their own leading icon columns; everything else — including
 * Open Reporting and Share Study — lives in here.
 */
const buildMoreMenu = (config: ActionColumnConfig, record: any) => {
  const userDetails = getUserDetails();
  const radUrl = CENTER_RADIOLOGY_URL;
  const studyUID = record?.ps_study_uid;
  const pacs_status = record?.order_workflow?.ow_pacs_status;
  const canPrintReport = pacs_status === 'SIGNEDOFF';
  const canViewDispatchReport = hasPermission(Permissions.VIEW_DISPATCH_REPORT);
  const canReportFromPacs = hasPermission(Permissions.REPORT_FROM_PACS);

  // Same rule as the leading report icon: a CANCELLED / NOT_FOR_REPORTING study can
  // never be opened for reporting, so don't offer the action at all.
  const isReportable =
    pacs_status !== 'CANCELLED' &&
    pacs_status !== 'NOT_FOR_REPORTING' &&
    record?.pacs_order?.po_his_status !== 'CANCELLED';
  const showOpenReporting = !!config.openReportEditor && canReportFromPacs && isReportable;
  const showShare = !!studyUID && !!config.handleShareStudy;

  const showExport = !!config.handleExportStudy;
  const showEdit = hasPermission(Permissions.MODIFY_STUDY_DETAILS) && !!config.editStudyDetails;
  const showMlc = !!config.markMlc;
  const showSplit =
    !!studyUID && !!config.splitStudy &&
    ['technician', 'radiologist', 'hod'].includes(userDetails?.user_type);
  const hasStudyActions = showExport || showEdit || showMlc || showSplit;

  const showHistory =
    !!config.showStudyHistory && !['radiologist', 'typist'].includes(userDetails?.user_type);
  const showRadDesk = !!radUrl && !!config.openRadDesk;
  const hasOtherActions = showHistory || showRadDesk;

  return (
    <Menu style={{ maxHeight: '40vh', overflowY: 'auto' }}>
      {/* Promoted primary actions */}
      {showOpenReporting && (
        <Menu.Item
          key="open-reporting"
          icon={<EditOutlined />}
          onClick={e => {
            e.domEvent.stopPropagation();
            config.openReportEditor && config.openReportEditor(record);
          }}
        >
          Open Reporting
        </Menu.Item>
      )}
      {showShare && (
        <Menu.Item
          key="share-study"
          icon={<ShareAltOutlined />}
          onClick={e => {
            e.domEvent.stopPropagation();
            config.handleShareStudy && config.handleShareStudy(record);
          }}
        >
          Share Study
        </Menu.Item>
      )}
      {(showOpenReporting || showShare) && <Menu.Divider />}

      {/* Report Actions — gated by the dispatch-report view permission */}
      {canViewDispatchReport && (
        <>
          <Menu.ItemGroup title="Report Actions">
            <Menu.Item
              key="view-report"
              icon={<EyeOutlined />}
              disabled={!canPrintReport}
              onClick={e => {
                e.domEvent.stopPropagation();
                if (canPrintReport && config.viewReport) {
                  config.viewReport(record);
                }
              }}
            >
              View Report
            </Menu.Item>
            <Menu.Item
              key="print-report"
              icon={<PrinterOutlined />}
              disabled={!canPrintReport}
              onClick={e => {
                e.domEvent.stopPropagation();
                if (canPrintReport && config.printReport) {
                  config.printReport(record);
                }
              }}
            >
              Print / Download PDF
            </Menu.Item>
            {(userDetails?.user_type === 'radiologist' ||
              userDetails?.user_type === 'hod' ||
              userDetails?.user_type === 'typist' || userDetails?.user_type === 'technician') && (
              <Menu.Item
                key="download-word"
                icon={<FileWordOutlined />}
                disabled={!canPrintReport}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  if (canPrintReport && config.downloadDocxReport) {
                    config.downloadDocxReport(record);
                  }
                }}
              >
                Download Word
              </Menu.Item>
            )}
          </Menu.ItemGroup>
          <Menu.Divider />
        </>
      )}

      {/* Study Actions (Open Viewer + Download Study are now leading icons) */}
      {hasStudyActions && (
        <Menu.ItemGroup title="Study Actions">
          {showExport && (
            <Menu.Item
              key="export-study"
              icon={<ExportOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.handleExportStudy && config.handleExportStudy(record);
              }}
            >
              Push Study
            </Menu.Item>
          )}
          {showEdit && (
            <Menu.Item
              key="edit-study"
              icon={<EditOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.editStudyDetails && config.editStudyDetails(record);
              }}
            >
              Edit Study Details
            </Menu.Item>
          )}
          {showMlc && (
            <Menu.Item
              key="mark-mlc"
              icon={<FlagOutlined style={{ color: record?.pacs_order?.po_is_mlc ? '#475569' : undefined }} />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.markMlc && config.markMlc(record);
              }}
            >
              {record?.pacs_order?.po_is_mlc ? 'Edit / Unmark MLC' : 'Mark as MLC'}
            </Menu.Item>
          )}
          {showSplit && (
            <Menu.Item
              key="split-study"
              icon={<SplitCellsOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.splitStudy && config.splitStudy(record);
              }}
            >
              Split Study
            </Menu.Item>
          )}
        </Menu.ItemGroup>
      )}

      {/* Other Actions (Patient & Order Info is now a leading icon) */}
      {hasOtherActions && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup title="Other Actions">
            {showHistory && (
              <Menu.Item
                key="study-history"
                icon={<ClockCircleOutlined />}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  config.showStudyHistory && config.showStudyHistory(record);
                }}
              >
                Study History Timeline
              </Menu.Item>
            )}
            {showRadDesk && (
              <Menu.Item
                key="radiology-desk"
                icon={<ExportOutlined />}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  config.openRadDesk && config.openRadDesk(record);
                }}
              >
                Radiology Desk
              </Menu.Item>
            )}
          </Menu.ItemGroup>
        </>
      )}

      {/* Danger Zone - soft-delete (env-gated, radiologist/HOD only). */}
      {config.canDeleteOrder && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup title="Danger Zone">
            <Menu.Item
              key="delete-order"
              danger
              icon={<DeleteOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.deleteOrder && config.deleteOrder(record);
              }}
            >
              Delete Order
            </Menu.Item>
          </Menu.ItemGroup>
        </>
      )}
    </Menu>
  );
};

/**
 * Base column definitions shared by both tables
 */
export const getBaseColumns = () => {
  const userDetails = getUserDetails();

  return [
    {
      dataIndex: 'po_pin',
      key: 'po_pin',
      title: getPatientIdLabel(),
      width: 105,
      render: (text, rec) => {
        const patPin = rec?.pacs_order?.patient?.pat_pin;
        return (
          <Tooltip title={patPin}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {patPin}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_pat_name',
      key: 'pat_name',
      title: FieldLabels.PATIENT_NAME,
      render: (text, record, openReportEditor, openViewer, openRadiantViewer, enableRadiantViewer) => {
        const currentlyBlocked = record.order_workflow?.ow_reporting_blocked === true;
        // Emergency cases are now indicated by a reddish row tint (getRowClass),
        // not an inline alert icon next to the patient name.
        const isMlc = record.pacs_order?.po_is_mlc === true;
        const patientName = record.pacs_order?.patient?.pat_name;

        const buttonContent = (
          <Button
            className="ms-1auto d-flex align-items-center ph-0 col-pat-name"
            type="link"
            onClick={() => {
              if (!currentlyBlocked && openReportEditor) {
                openReportEditor(record);
                // openViewer(record);
              }
            }}
            style={{ padding: 0 }}
          >
            <Tooltip title={patientName}>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                maxWidth: isMlc ? 'calc(100% - 34px)' : '100%'
              }}>
                {patientName}
              </span>
            </Tooltip>
            {isMlc && (
              <Tooltip title={getMlcTooltip(record.pacs_order)}>
                <span
                  style={{
                    marginLeft: '6px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: '15px',
                    padding: '0 2px',
                    fontSize: '9px',
                    fontWeight: 700,
                    lineHeight: '15px',
                    letterSpacing: '0',
                    color: '#fff',
                    background: '#475569',
                    borderRadius: '3px'
                  }}
                >
                  M
                </span>
              </Tooltip>
            )}
          </Button>
        );

        // Always provide a right-click context menu: at minimum a "Copy Study UID"
        // action. The Radiant Viewer entry is added only when Radiant is enabled.
        const studyUID = record?.ps_study_uid;
        const contextMenu = (
          <Menu>
            <Menu.Item
              key="copy-study-uid"
              disabled={!studyUID}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                if (!studyUID) {
                  return;
                }
                navigator.clipboard
                  .writeText(studyUID)
                  .then(() => message.success('Study UID copied'))
                  .catch(() => message.error('Failed to copy Study UID'));
              }}
            >
              Copy Study UID
            </Menu.Item>
            {enableRadiantViewer && openRadiantViewer && (
              <Menu.Item
                key="radiant"
                disabled={!studyUID}
                onClick={(e) => {
                  e.domEvent.stopPropagation();
                  if (studyUID) {
                    openRadiantViewer(studyUID);
                  }
                }}
              >
                Open in Radiant Viewer
              </Menu.Item>
            )}
          </Menu>
        );

        // The Dropdown trigger must cover the whole cell — wrapping only the inline
        // <Button> leaves the surrounding cell area un-triggered, so right-clicking
        // there falls through to the browser's native menu. The full-width wrapper
        // (plus an explicit preventDefault) makes the entire cell the trigger and
        // suppresses the default menu reliably inside the AG Grid cell.
        return (
          <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
            <div
              style={{ width: '100%', display: 'flex', alignItems: 'center' }}
              onContextMenu={e => e.preventDefault()}
            >
              {buttonContent}
            </div>
          </Dropdown>
        );
      },
      width: 250,
    },
    {
      dataIndex: 'modality',
      key: 'modality',
      title: FieldLabels.MODALITY,
      width: 100,
      render: (text, rec) => {
        const po = rec?.pacs_order;
        // For "any-of" groups (x-ray, where DX and CR are interchangeable and
        // only one is ever produced) show the modality that actually arrived —
        // stamped into po_reconcile_check by reevaluateOrderReconciliation —
        // instead of the ordered group csv "DX,CR". All-of groups (hybrids like
        // PET-CT) keep showing the full po_modality group, as does Orders.
        let display = po?.po_modality;
        const check = po?.po_reconcile_check;
        if (check?.match_type === 'ANY' && Array.isArray(check?.checks)) {
          const modCheck = check.checks.find((c) => c?.field === 'modality');
          if (modCheck?.received) display = modCheck.received;
        }
        return (
          <span
            style={{
              background: '#F5F5F5',
              border: '1px solid #EAEEF2',
              height: '24px',
              borderRadius: '4px',
              padding: '2px 8px',
              display: 'inline-block',
              lineHeight: '20px',
              // Grid-wide wrap CSS (word-break: break-word) can split short codes
              // like "MR" letter-by-letter once the column hits its minWidth on
              // small/scaled displays — keep the badge on one line.
              whiteSpace: 'nowrap',
            }}
          >
            {display}
          </span>
        );
      },
    },
    {
      dataIndex: 'po_body_part',
      key: 'body_part',
      title: FieldLabels.BODY_PART,
      width: 120,
      render: (text, rec) => {
        const bodyPart = rec?.pacs_order?.po_body_part;
        return (
          <Tooltip title={bodyPart}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {bodyPart}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_diag_desc',
      title: FieldLabels.STUDY_DESCRIPTION,
      key: 'diag_desc',
      width: 200,
      render: (txt, rec) => {
        const studyDesc = rec?.pacs_order?.po_diag_desc;
        const priority = rec.pacs_order?.po_priority;

        return (
          <Tooltip title={`${studyDesc}${priority ? ` (Priority: ${priority})` : ''}`}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {studyDesc}
            </div>
          </Tooltip>
        );
      },
    },
    // {
    //   dataIndex: 'ps_series_count',
    //   title: FieldLabels.SERIES_COUNT,
    //   key: 'ps_series_count',
    //   width: 105,
    //   render: (txt, rec) => {
    //     const ps_series_count = rec?.ps_series_count || 0;
    //     const ps_image_count = rec?.ps_image_count || 0;
    //     const isIngesting = !rec?.is_study_ingestion_complete;
    //     return (
    //       <Tooltip
    //         title={
    //           isIngesting
    //             ? 'Images are still being received from modality'
    //             : `Series: ${ps_series_count}, Images: ${ps_image_count}`
    //         }
    //       >
    //         <div
    //           style={{
    //             display: 'flex',
    //             justifyContent: 'flex-end',
    //             alignItems: 'center',
    //             gap: 6,
    //             width: '100%',
    //           }}
    //         >
    //           <span>
    //             {ps_series_count} / {ps_image_count}
    //           </span>

    //           {/* {isIngesting ? (
    //             <HourglassOutlined
    //               spin
    //               style={{ fontSize: 14, color: '#1890ff' }}
    //             />
    //           ) : (
    //             <CheckCircleOutlined style={{ fontSize: 14, color: '#52c41a' }} />
    //           )}  */}
    //         </div>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      dataIndex: 'po_ord_no',
      key: 'po_ord_no',
      title: FieldLabels.ORDER_NUMBER,
      width: 100,
      render: (text, rec) => rec?.pacs_order?.po_his_ord_no,
    },
    {
      dataIndex: 'po_acc_no',
      key: 'acc_no',
      title: FieldLabels.ACCESSION_NUMBER,
      width: 120,
      render: (text, rec) => {
        const accNo = rec?.pacs_order?.po_acc_no;
        return (
          <Tooltip title={accNo}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {accNo}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_pat_age',
      key: 'pat_age',
      title: FieldLabels.PATIENT_REPORT_AGE,
      width: 100,
      render: (val, record) =>
        formatAgeShort(
          record.pacs_order?.patient?.pat_dob,
          record.pacs_order?.patient?.pat_age
        ),
    },
    {
      dataIndex: 'po_pat_sex',
      key: 'pat_sex',
      title: FieldLabels.PATIENT_REPORT_SEX,
      width: 100,
      render: (text, rec) => rec?.pacs_order?.patient?.pat_sex,
    },
    {
      dataIndex: 'po_ref_doc',
      key: 'ref_doc',
      title: FieldLabels.REFERRING_DOCTOR,
      width: 175,
      render: (text, rec) => {
        const refDoc = rec?.pacs_order?.po_ref_doc;
        return (
          <Tooltip title={refDoc}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign:refDoc?'left':'center',
              }}
            >
              {refDoc||'-'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'ps_study_dt_tm',
      key: 'study_dt_tm',
      title: FieldLabels.SCAN_DATE,
      render: val => <StudyDateTime value={val} />,
      width: 200,
      // Hidden on PACS list & My Worklist (the only consumers of getBaseColumns).
      hidden: true,
    },
    {
      dataIndex: 'ps_scan_received_at',
      key: 'scan_received_at',
      title: FieldLabels.RECEIVED_DATE,
      width: 200,
      render: val => <StudyDateTime value={val} />,
    },
    {
      dataIndex: 'po_status',
      key: 'status',
      title: FieldLabels.STATUS,
      fixed: 'right',
      className: 'first-sticky-column',
      render: (text, record) => {
        const pacs_status = record?.order_workflow?.ow_pacs_status;
        const isReReported = record?.order_workflow?.ow_re_reported;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Tag
              className="custom-status-tag"
              style={getStatusStyle(pacs_status)}
            >
              {getPacsStatusLabel(pacs_status)}
            </Tag>
            {isReReported && <ReReportedBadge />}
          </div>
        );
      },
      width: 130,
    },
    {
      dataIndex: 'po_his_status',
      key: 'po_his_status',
      title: 'HIS Status',
      // Sized to the widest rendered pill (UNCONFIRMED ≈ 91px at the 9px pill
      // font) + 24px cell padding ≈ 115px. The Confirm chip is narrower (~87px);
      // fitting the widest *state* is what keeps any status from clipping.
      width: 116,
      hidden: !(Boolean(CENTER_RADIOLOGY_URL)),
      // RECONCILED renders as a green badge; a stored multi-field MISMATCH
      // (po_reconcile_check) renders a "Confirm" button that opens the
      // field-comparison dialog with optional accession correction.
      render: (text, rec) => <HisStatusReconcileCell rec={rec} />,
    },
    {
      dataIndex: 'pr_status',
      key: 'live_report_status',
      title: ' ',
      width: 30,
      render: (text, rec) => (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <ReportingNowBadge
            status={rec?.order_workflow?.ow_reporting_status}
            // Backend-resolved full name + raw username of whoever currently
            // holds the study open; the badge renders them as "name (username)".
            reporterName={rec?.reportingNowBy}
            reporterUsername={rec?.order_workflow?.ow_currently_opened_by}
          />
        </div>
      ),
    },
  ];
};

/**
 * PACS-specific columns (not in worklist)
 */
export const getPacsSpecificColumns = () => {
  const userDetails = getUserDetails();

  return [
    {
      dataIndex: 'ps_sending_aet',
      key: 'sending_aet',
      title: FieldLabels.AE_TITLE,
      width: 120,
      hidden: userDetails?.user_type === 'technician',
    },
    {
      dataIndex: '',
      key: 'reported_by',
      title: FieldLabels.REPORTED_BY,
      width: 150,
      render: (text, rec) => {
        const user_name = rec?.reportedBy || '';
        return (
          <Tooltip title={user_name}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {user_name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_signed_by',
      key: 'signed_by',
      title: FieldLabels.SIGNED_BY,
      render: (text, record) => {
        const user_name = record?.signedBy || '';
        return (
          <Tooltip title={user_name}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {user_name}
            </div>
          </Tooltip>
        );
      },
      width: 150,
      hidden: userDetails?.user_type === 'technician',
    },
    {
      dataIndex: 'assigned_to',
      key: 'assigned_to_tech',
      title: FieldLabels.TECHNICIAN,
      // Read-only on the study list — technicians are assigned from the Orders
      // page (which owns the assignment dropdown). Here we only display who the
      // study is currently assigned to.
      render: (text, record) => (
        <span>{record?.order_workflow?.ow_assigned_technician || '-'}</span>
      ),
      width: 150,
      hidden: userDetails?.user_type !== 'technician',
    },
    {
      dataIndex: 'assigned_radiologist',
      key: 'assigned_radiologist',
      title: FieldLabels.ASSIGNED_RADIOLOGIST,
      render: (text, record, ind, handleRadiologistAssignment, userList) => {
        const assignedRadId = record?.order_workflow?.ow_assigned_rad_id;
        // Radiologists cannot (re)assign cases even if they hold ASSIGN_CASES —
        // the assignment dropdown is reserved for HOD/coordinators.
        const isRadiologist = userDetails?.user_type === 'radiologist';
        const canAssign = hasPermission(Permissions.ASSIGN_CASES) && !isRadiologist;

        if (canAssign) {
          // Show dropdown for users with ASSIGN_CASES permission
          return (
            <Select
              value={assignedRadId || undefined}
              placeholder="Unassigned"
              allowClear
              style={{ width: '100%', minWidth: '150px' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={userList
                ?.filter(u => u.user_type === 'radiologist')
                .map(user => ({
                  label: user.user_fullname,
                  value: user.username,
                }))}
              onChange={value =>
                handleRadiologistAssignment && handleRadiologistAssignment(record, value, ind)
              }
              onClick={e => e.stopPropagation()}
            />
          );
        } else {
          // Show plain text for users without permission
          return <span>{assignedRadId || '-'}</span>;
        }
      },
      width: 180,
      hidden: false,
    },
  ];
};

/* =============================================================================
 * Reference (RIS-style) flex layout — shared by the PACS list and the Worklist.
 *
 * Radiologists hate horizontal scrolling, so every column is given a `flex`
 * weight instead of a fixed pixel width. AG Grid then distributes the available
 * viewport width across the columns in proportion to those weights, so the table
 * always fills the viewport exactly — no horizontal scrollbar in the common
 * case. The weights are derived from the legacy RIS column-width percentages
 * (Patient Name 24%, Ref. Phys. 12%, Date/Time 8%, Status 6%, Sex 2%, …). On a
 * narrow screen columns compress down to `minWidth`; only then does a scrollbar
 * appear. The reporting-status sliver stays a fixed, un-flexed width.
 * ===========================================================================*/

type RefLayout = { flex?: number; minWidth?: number; width?: number };

const REFERENCE_LAYOUT: Record<string, RefLayout> = {
  pat_name: { flex: 24, minWidth: 185 },
  po_pin: { flex: 6, minWidth: 88 },
  pat_age: { flex: 3, minWidth: 56 },
  pat_sex: { flex: 2, minWidth: 44 },
  body_part: { flex: 6, minWidth: 70 },
  diag_desc: { flex: 8, minWidth: 90 },
  po_site: { flex: 5, minWidth: 70 },
  sending_aet: { flex: 6, minWidth: 80 },
  // 24px cell padding + 18px badge chrome. 64 fit a 2-char code but wrapped
  // multi-modality csv values ("CT,PT" from T002 item groups); 86 fits the
  // 5-char csv on one line without stealing much from the trailing columns.
  modality: { flex: 4, minWidth: 86 },
  po_ord_no: { flex: 5, minWidth: 80 },
  po_visit_number: { flex: 5, minWidth: 70 },
  acc_no: { flex: 6, minWidth: 88 },
  ref_doc: { flex: 12, minWidth: 110 },
  // Stacked date-over-time cell (see <StudyDateTime/>): widest line is the date
  // ("14.07.2026"), so these need far less width than the old single-line format.
  study_dt_tm: { flex: 6, minWidth: 92 },
  scan_received_at: { flex: 6, minWidth: 92 },
  // ps_series_count: { flex: 4, minWidth: 66 },
  // Floor fits the widest pill ("UNREPORTED") at the enlarged 13px bold
  // status font (~96px) + 24px cell padding, so no status clips when the
  // flex layout compresses to a narrow viewport.
  status: { flex: 7.5, minWidth: 128 },
  // Floor = widest pill (UNCONFIRMED ≈ 91px) + 24px cell padding ≈ 115px, so no
  // status clips even when the flex layout shrinks to the viewport. Lower flex
  // weight (was 6) keeps it tight rather than over-growing on wide screens.
  po_his_status: { flex: 4, minWidth: 116 },
  reported_by: { flex: 6, minWidth: 90 },
  signed_by: { flex: 6, minWidth: 90 },
  assigned_to_tech: { flex: 7, minWidth: 120 },
  assigned_radiologist: { flex: 8, minWidth: 130 },
  // Reporting-now dot — fixed sliver matching the leading action-icon columns.
  live_report_status: { width: 26, minWidth: 26 },
};

// Column order mirroring the reference RIS study list. Patient Name leads
// (after the leading action-icon cluster), HIS Status sits right after the
// patient IDs, Ref. Phys. follows Acc. No., PACS Status ("Details") sits at
// the end (after Received Date), and the "who-acted / assigned" group trails.
// Keys not present for a given role/page (or flagged `hidden`) are filtered out.
const REFERENCE_ORDER = [
  // Reporting-now dot sits just before the name.
  'live_report_status', 'pat_name', 'po_pin', 'po_his_status', 'pat_age', 'pat_sex',
  'body_part', 'diag_desc', 'po_site', 'sending_aet', 'modality',
  'po_ord_no', 'po_visit_number', 'acc_no', 'ref_doc',
  'study_dt_tm', 'scan_received_at', 'ps_series_count', 'status',
  'reported_by', 'signed_by', 'assigned_to_tech', 'assigned_radiologist',
];

// A new column key whose presence in a saved column-order marks it as
// "new-layout aware". Existing saved preferences predate the reference layout
// and won't contain it, so callers can detect & discard stale prefs once.
export const REFERENCE_LAYOUT_MARKER = 'po_site';

// Two-line clamp (wrap once, then ellipsis) for the synthesised text columns.
const refEllipsis: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  width: '100%',
  lineHeight: 1.2,
};

/**
 * Re-order an Ant-style column array into the reference RIS layout and convert
 * every column to a flex weight (fit-to-viewport). Mutates/returns fresh column
 * objects — callers pass the result of getBaseColumns()/getPacsSpecificColumns(),
 * which are created anew on each call, so this is safe.
 *
 * - Adds the Site + Visit ID columns (sourced from data the list APIs already
 *   return) when they aren't already present.
 * - Un-pins the Status column so it sits inline ("Details") as in the reference.
 * - AE-Title is shown for every role in this layout.
 */
export const applyReferenceLayout = (columns: any[]): any[] => {
  const byKey: Record<string, any> = {};
  columns.forEach(c => {
    byKey[c.key || c.dataIndex] = c;
  });

  // AE-Title is part of the reference layout for everyone.
  if (byKey['sending_aet']) byKey['sending_aet'].hidden = false;

  // Status sits inline (reference "Details"), not pinned to the right.
  if (byKey['status']) {
    byKey['status'].fixed = undefined;
    byKey['status'].className = undefined;
  }

  // Site + Visit ID — only synthesised if the caller didn't already supply them.
  if (!byKey['po_site']) {
    byKey['po_site'] = {
      dataIndex: 'po_site',
      key: 'po_site',
      title: FieldLabels.SITE,
      render: (_t: any, rec: any) => {
        const site = rec?.pacs_order?.po_site || rec?.pacs_order?.po_center_name || '';
        return <Tooltip title={site}><div style={refEllipsis}>{site}</div></Tooltip>;
      },
    };
  }
  if (!byKey['po_visit_number']) {
    byKey['po_visit_number'] = {
      dataIndex: 'po_visit_number',
      key: 'po_visit_number',
      title: FieldLabels.VISIT_ID,
      render: (_t: any, rec: any) => {
        const visit = rec?.pacs_order?.po_visit_number || '';
        return <Tooltip title={visit}><div style={refEllipsis}>{visit}</div></Tooltip>;
      },
    };
  }

  return REFERENCE_ORDER
    .map(k => byKey[k])
    .filter(col => col && !col.hidden)
    .map(col => {
      const layout = REFERENCE_LAYOUT[col.key || col.dataIndex];
      if (layout) {
        if (layout.flex != null) {
          col.flex = layout.flex;
          col.width = undefined; // flex wins; drop the legacy fixed width
        }
        if (layout.width != null) col.width = layout.width;
        if (layout.minWidth != null) col.minWidth = layout.minWidth;
      }
      return col;
    });
};

/**
 * Build the leading (left-pinned) action columns rendered immediately after the
 * checkbox, before Patient Name — mirroring the reference layout where the action
 * icons live on the left for every role.
 *
 * - Technicians get three slim icon columns (View Notes / Patient & Order Info /
 *   Study History) — their trimmed action set, matching the Orders tab.
 * - Everyone else gets a row of slim icon columns mirroring old PACS — View
 *   Notes / Patient & Order Info / Download Study / Open Viewer / Select Series —
 *   followed by a More-actions kebab (buildMoreMenu) holding the rest, including
 *   Open Reporting and Share Study.
 */
export const buildLeadingActionColumns = (config: ActionColumnConfig): any[] => {
  const userDetails = getUserDetails();
  const isTechnician = userDetails?.user_type === 'technician';
  // Compact icon-button box — slim columns with minimal padding so the icon
  // fills the button and stays clearly visible (frees horizontal space for data
  // columns while keeping the glyphs large).
  const spanBase: React.CSSProperties = { padding: '1px', width: '24px', height: '24px' };

  const cols: any[] = [];
  // Shared columns (View Notes and Patient Info) available to all roles
  if (config.viewNotes) {
    cols.push({
      key: 'notes_icon',
      width: 26,
      render: (_v: any, rec: any) => {
        const hasNotes = rec?.note_id;
        return (
          <Tooltip title="View Notes">
            <span
              className="action-icon-button"
              style={{ ...spanBase, ...hoverVar('notes') }}
              onClick={e => {
                e.stopPropagation();
                config.viewNotes(rec);
              }}
            >
              <NotesIcon size={22} disabled={false} variant={hasNotes ? 'blue' : 'grey'} />
            </span>
          </Tooltip>
        );
      },
    });
  }

  if (config.showOrdDetails) {
    cols.push({
      key: 'info_icon',
      width: 26,
      render: (_v: any, rec: any) => (
        <Tooltip title="Patient & Order Info">
          <span
            className="action-icon-button"
            style={{ ...spanBase, ...hoverVar('info') }}
            onClick={e => {
              e.stopPropagation();
              config.showOrdDetails!(rec);
            }}
          >
            <InfoIcon size={22} />
          </span>
        </Tooltip>
      ),
    });
  }

  if (isTechnician) {
    // Documentation Uploads — same dialog (notes + file upload) the technician
    // gets on the Orders tab, exposed here too so a scan can be documented from
    // the Scan List without switching tabs. Same CREATE_TECH_NOTES gate.
    if (config.addFile && hasPermission(Permissions.CREATE_TECH_NOTES)) {
      cols.push({
        key: 'upload_icon',
        width: 26,
        render: (_v: any, rec: any) => (
          <Tooltip title="Documentation Uploads">
            <span
              className="action-icon-button"
              style={{ ...spanBase, ...hoverVar('upload') }}
              onClick={e => {
                e.stopPropagation();
                config.addFile!(rec);
              }}
            >
              <UploadIcon size={22} />
            </span>
          </Tooltip>
        ),
      });
    }
    // Series info (T009) — read-only dialog listing each series' description,
    // modality and image count. Mirrors the radiologist's Select-Series icon but
    // opens an info-only variant (no selection / viewer launch).
    // Commented as Select-Series(selection and viewer launch) is present for technician
    // if (config.handleSeriesInfo) {
    //   cols.push({
    //     key: 'series_info_icon',
    //     width: 26,
    //     render: (_v: any, rec: any) => {
    //       const studyUID = rec?.ps_study_uid;
    //       if (!studyUID) return null;
    //       return (
    //         <Tooltip title="Series Info">
    //           <span
    //             className="action-icon-button"
    //             style={{ ...spanBase, ['--ai-hover' as any]: ACTION_HOVER.series }}
    //             onClick={e => {
    //               e.stopPropagation();
    //               config.handleSeriesInfo!(rec);
    //             }}
    //           >
    //             <SeriesIcon size={22} />
    //           </span>
    //         </Tooltip>
    //       );
    //     },
    //   });
    // }
    if (config.showStudyHistory) {
      cols.push({
        key: 'history_icon',
        width: 26,
        render: (_v: any, rec: any) => (
          <Tooltip title="Study History Timeline">
            <span
              className="action-icon-button"
              style={{ ...spanBase, ['--ai-hover' as any]: ACTION_HOVER.history }}
              onClick={e => {
                e.stopPropagation();
                config.showStudyHistory!(rec);
              }}
            >
              <HistoryIcon size={22} />
            </span>
          </Tooltip>
        ),
      });
    }
    return cols;
  }

  // Non-technician (radiologist / HOD / typist): individual narrow leading icon
  // columns mirroring old PACS — Download Study, Open Viewer, Select Series, then
  // a More-actions kebab holding the rest (including Open Reporting + Share Study).
  // Each column is a 34px pinned-left sliver, in the same left-to-right order as
  // the old PACS study list.

  if (config.handleDownloadStudy && hasPermission(Permissions.EXPORT_IMAGE)) {
    cols.push({
      key: 'download_icon',
      width: 26,
      render: (_v: any, rec: any) => (
        <Tooltip title="Download Study">
          <span
            className="action-icon-button"
            style={{ ...spanBase, ...hoverVar('download') }}
            onClick={e => { e.stopPropagation(); config.handleDownloadStudy!(rec); }}
          >
            <DownloadIcon size={22} />
          </span>
        </Tooltip>
      ),
    });
  }
  if (config.openReportEditor && hasReportingPermission(userDetails)) {
    cols.push({
      key: 'report_icon',
      width: 26,
      render: (_v: any, rec: any) => {
        // CANCELLED and NOT_FOR_REPORTING studies can never be opened for reporting
        // (openReport() hard-blocks both), so the icon is dropped entirely rather than
        // shown as a dead control. The sliver itself stays so columns keep aligning.
        const status = rec?.order_workflow?.ow_pacs_status;
        if (status === 'CANCELLED' || status === 'NOT_FOR_REPORTING'
          || rec?.pacs_order?.po_his_status === 'CANCELLED') {
          return null;
        }

        // With BLOCK_UNCONFIRMED_REPORTING on, an unconfirmed study is a dead end, so render
        // the icon disabled rather than letting the click fail. With it off the icon stays
        // live — openReport() raises the acknowledgement dialog — and only the tooltip warns,
        // so the prompt isn't a surprise.
        const isUnconfirmed = rec?.pacs_order?.po_his_status === 'UNCONFIRMED';
        const isBlocked = isUnconfirmed && isUnconfirmedReportingBlocked();
        const tone = getReportIconTone(status);
        return (
          <Tooltip
            title={
              isBlocked
                ? 'Cannot report unconfirmed study'
                : isUnconfirmed
                  ? `${REPORT_TONE_LABEL[tone]} — study not confirmed in HIS`
                  : REPORT_TONE_LABEL[tone]
            }
          >
            <span
              className={`action-icon-button ${isBlocked ? 'disabled' : ''}`}
              style={{ ...spanBase, ...(isBlocked ? {} : hoverVar('report')) }}
              onClick={e => {
                e.stopPropagation();
                if (!isBlocked) {
                  openReport(rec, userDetails, true);
                }
              }}
            >
              <ReportIcon size={22} disabled={isBlocked} tone={tone} />
            </span>
          </Tooltip>
        );
      },
    });
  }

  if (config.openViewer) {
    cols.push({
      key: 'viewer_icon',
      width: 26,
      render: (_v: any, rec: any) => (
        <Tooltip title="Open Viewer">
          <span
            className="action-icon-button"
            style={{ ...spanBase, ...hoverVar('viewer') }}
            onClick={e => { e.stopPropagation(); config.openViewer!(rec); }}
          >
            <ViewerIcon size={22} />
          </span>
        </Tooltip>
      ),
    });
  }

  // if (config.handleSelectSeries) {
  //   cols.push({
  //     key: 'series_icon',
  //     width: 26,
  //     render: (_v: any, rec: any) => {
  //       const studyUID = rec?.ps_study_uid;
  //       if (!studyUID) return null;
  //       return (
  //         <Tooltip title="Select Series">
  //           <span
  //             className="action-icon-button"
  //             style={{ ...spanBase, ...hoverVar('series') }}
  //             onClick={e => { e.stopPropagation(); config.handleSelectSeries!(rec); }}
  //           >
  //             <SeriesIcon size={22} />
  //           </span>
  //         </Tooltip>
  //       );
  //     },
  //   });
  // }

  // More-actions kebab — always present for non-technician roles. Opens to the
  // right (bottomLeft) so it doesn't spill off the left edge of the table.
  // cols.push({
  //   key: 'more_icon',
  //   width: 26,
  //   render: (_v: any, rec: any) => (
  //     <Dropdown overlay={buildMoreMenu(config, rec)} trigger={['click']} placement="bottomLeft">
  //       <Tooltip title="More Actions">
  //         <span
  //           className="action-icon-button"
  //           style={{ ...spanBase, ...hoverVar('more') }}
  //           onClick={e => e.stopPropagation()}
  //         >
  //           <MoreIcon size={22} />
  //         </span>
  //       </Tooltip>
  //     </Dropdown>
  //   ),
  // });

  return cols;
};
